// frontend/controllers/mobile_nav_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "backdrop"]
  static values = {
    openClass:   { type: String, default: "is-open" },
    lockedClass: { type: String, default: "is-fixed" },
    oneOpen:     { type: Boolean, default: false } // set to true to allow only one open at a time
  }

  connect() {
    // Ensure clean state for the drawer itself
    this.close()

    // Normalize all accordions' aria-expanded from current data-open state
    this.syncAllAccordions()

    // Turbo lifecycle
    this.beforeCacheHandler = () => this.close()
    document.addEventListener("turbo:before-cache", this.beforeCacheHandler)

    this.loadHandler = () => {
      // page navigation (Turbo) — keep server-preopened subnavs, just resync aria
      this.syncAllAccordions()
      this.close()
    }
    document.addEventListener("turbo:load", this.loadHandler)

    // Esc to close the drawer
    this.keyHandler = (e) => { if (e.key === "Escape") this.close() }
    document.addEventListener("keydown", this.keyHandler)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCacheHandler)
    document.removeEventListener("turbo:load", this.loadHandler)
    document.removeEventListener("keydown", this.keyHandler)
    this.releaseScroll()
  }

  // === Drawer open/close ===
  open(event) {
    if (event) event.preventDefault()
    this.element.classList.add(this.openClassValue)
    this.lockScroll()
    this.trapFocus()
  }

  close(event) {
    if (event) event.preventDefault()
    this.element.classList.remove(this.openClassValue)
    this.releaseScroll()
    this.releaseFocus()
  }

  toggle(event) {
    if (this.element.classList.contains(this.openClassValue)) {
      this.close(event)
    } else {
      this.open(event)
    }
  }

  closeOnBackdrop(event) {
    if (event.target === this.backdropTarget) this.close(event)
  }

  closeOnLinkClick(event) {
    const a = event.target.closest("a[href]")
    if (a) this.close()
  }

  // === Accordion logic ===
  toggleAccordion(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const li = button.closest("li.accordion")
    if (!li) return

    const isOpen = (li.dataset.open === "true")

    if (isOpen) {
      this.closeAccordion(li)
    } else {
      if (this.oneOpenValue) this.closeAllAccordionsExcept(li)
      this.openAccordion(li)
    }
  }

  openAccordion(li) {
    li.dataset.open = "true"
    this.syncAria(li)
  }

  closeAccordion(li) {
    li.dataset.open = "false"
    this.syncAria(li)
  }

  closeAllAccordionsExcept(keepLi) {
    this.accordionItems().forEach((item) => {
      if (item !== keepLi) this.closeAccordion(item)
    })
  }

  syncAllAccordions() {
    this.accordionItems().forEach((li) => this.syncAria(li))
  }

  syncAria(li) {
    // Find the toggle button inside this li and mirror state to aria-expanded
    const btn = li.querySelector('button[data-action~="mobile-nav#toggleAccordion"]')
    if (btn) btn.setAttribute("aria-expanded", li.dataset.open === "true" ? "true" : "false")
  }

  accordionItems() {
    return Array.from(this.element.querySelectorAll("li.accordion"))
  }

  // === Helpers ===
  lockScroll() {
    document.body.classList.add(this.lockedClassValue)
  }

  releaseScroll() {
    document.body.classList.remove(this.lockedClassValue, "is-fixed", "h-screen", "touch-none")
  }

  trapFocus() {
    this.prevActiveEl = document.activeElement
    const focusable = this.panelTarget?.querySelector(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable) focusable.focus()
  }

  releaseFocus() {
    if (this.prevActiveEl && typeof this.prevActiveEl.focus === "function") {
      this.prevActiveEl.focus()
    }
  }
}
